/**
 * <TotpForm> — the authenticator-app challenge step of tenant login.
 *
 * Rendered after LoginForm's `onTotpRequired` fires (login returned
 * `kind: "totp-required"`). Same route (/login), different card content —
 * the app's login page owns the step state; this component is purely the
 * TOTP entry view. Mirrors <OtpForm>.
 *
 * Per the be-602/be-603 contract:
 *   - Single code `<Input>` accepting a 6-digit authenticator code OR a
 *     recovery code (6–16 chars). A "use a recovery code" toggle switches the
 *     input hint + length + inputmode; `verifyTotp` validation stays permissive.
 *   - `autoComplete="one-time-code"`, `font-mono tabular-nums`, `dir="ltr"`.
 *   - Error branching: TOTP_INVALID_CODE / TOTP_RECOVERY_CODE_INVALID → inline
 *     "invalid" message (stay on step); TOTP_SESSION_INVALID / _EXPIRED (incl.
 *     the bodyless-401 restart case) → bounce to the credentials step via
 *     `onSessionExpired`; TOTP_SETUP_REQUIRED → inline "not enrolled" message.
 *   - Focus moves to the code input on mount.
 *
 * Security boundary: the access token is minted by the BACKEND in
 * `verifyTotp` — the login step NEVER issues one pre-verification. This file
 * holds no token state.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { match, P } from "ts-pattern";
import { Alert, Button, Form, FormField, Input } from "@linkhr/design-system";
import { totpSchema, type TotpSchema } from "../schemas/authSchemas.js";
import type { AuthSnapshot, Audience } from "../types.js";
import { useAuth } from "../react/useAuth.js";
import { ADMIN_AUDIENCE_ERROR_CODE, AdminAudienceNotice } from "./AdminAudienceNotice.js";
import { accountingRefusalKey } from "../errors/authErrorCodes.js";

export interface TotpFormProps {
  totpSessionToken: string;
  apiBaseUrl: string;
  onSuccess: (snapshot: AuthSnapshot) => void;
  onBack: () => void;
  /**
   * Called when the short-lived TOTP-session JWT is expired / invalid (incl.
   * the bodyless-401 undecodable-token case). The page bounces back to the
   * credentials step and shows a "session expired" notice.
   */
  onSessionExpired: () => void;
  /**
   * Suppress the form's internal `<h1>` + sub-copy when the surrounding page
   * renders the canonical title/subtitle in the card chrome.
   */
  hideHeading?: boolean;
  /**
   * Id of the heading the form's `aria-labelledby` points at. When the page
   * owns the heading (`hideHeading`), pass the page's heading id.
   * @default "totp-heading"
   */
  headingId?: string;
  /**
   * Audience to verify under. Omit — as all three per-audience apps do — and
   * the app's own audience is used. The global login passes the audience the
   * credentials were accepted for, which it only learns at login time.
   */
  audience?: Audience;
  /** Email typed at the password step, so the signed-in shell can name the user. */
  emailHint?: string;
}

export function TotpForm({
  totpSessionToken,
  apiBaseUrl,
  onSuccess,
  onBack,
  onSessionExpired,
  hideHeading = false,
  headingId = "totp-heading",
  audience,
  emailHint,
}: TotpFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const [serverErrorKey, setServerErrorKey] = useState<string | null>(null);
  // The wait a rate-limited code check names (the `seconds` of the rate-limit sentence).
  const [waitSeconds, setWaitSeconds] = useState(0);
  // be-788 / CC-431: AUTH_ADMIN_AUDIENCE_NOT_PERMITTED renders the SERVER's
  // pre-localized title + an employee-app link, never a generic error.
  const [audienceError, setAudienceError] = useState<string | null>(null);
  // Toggle between authenticator code (6 digits) and recovery code (6–16 chars).
  const [recoveryMode, setRecoveryMode] = useState(false);

  const methods = useForm<TotpSchema>({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const codeRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  const onSubmit = methods.handleSubmit(async (values) => {
    setServerErrorKey(null);
    setAudienceError(null);
    try {
      const result = await auth.completeTotp({
        totpSessionToken,
        code: values.code,
        apiBaseUrl,
        ...(audience !== undefined ? { audience } : {}),
        ...(emailHint !== undefined ? { emailHint } : {}),
      });
      onSuccess(result.snapshot);
    } catch (err) {
      const apiErr =
        err !== null && typeof err === "object" ? (err as Record<string, unknown>) : {};
      const errorCode = String((apiErr.errorCode as string | undefined) ?? "");
      // ApiError sets message = the RFC 7807 title — the server's pre-localized
      // copy at this seam. Branch the audience 403 BEFORE the session-expiry
      // bounce and the generic mapping: the code was fine, the surface isn't.
      if (errorCode === ADMIN_AUDIENCE_ERROR_CODE) {
        const serverTitle = typeof apiErr.title === "string" ? apiErr.title.trim() : "";
        setAudienceError(serverTitle !== "" ? serverTitle : t("session.admin_access_revoked"));
        return;
      }
      // A dead session bounces to the credentials step (with a notice) rather
      // than showing an inline error — the code the user typed is irrelevant
      // once the 5-minute session JWT is gone.
      if (errorCode === "TOTP_SESSION_INVALID" || errorCode === "TOTP_SESSION_EXPIRED") {
        onSessionExpired();
        return;
      }
      // Too many wrong codes for this person: say so, with the wait, never "invalid code".
      if (errorCode === "AUTH_RATE_LIMITED" || apiErr.status === 429) {
        const seconds = typeof apiErr.retryAfterSeconds === "number" ? apiErr.retryAfterSeconds : 0;
        setWaitSeconds(seconds);
        setServerErrorKey(
          seconds > 0 ? "login.error.rate_limited_retry" : "login.error.rate_limited",
        );
        return;
      }
      // useTranslation("auth") scopes keys — no "auth." prefix here.
      const key = match(errorCode)
        .with(
          P.union("TOTP_INVALID_CODE", "TOTP_RECOVERY_CODE_INVALID"),
          () => "totp.error.invalid",
        )
        .with("TOTP_SETUP_REQUIRED", () => "totp.error.setup_required")
        .otherwise(() => accountingRefusalKey(errorCode) ?? "error.network");
      setServerErrorKey(key);
    }
  });

  const isSubmitting = methods.formState.isSubmitting;

  const toggleRecoveryMode = (): void => {
    setRecoveryMode((prev) => !prev);
    setServerErrorKey(null);
    methods.setValue("code", "");
    methods.clearErrors("code");
    codeRef.current?.focus();
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit} aria-labelledby={headingId}>
        {!hideHeading && (
          <>
            <h1 id={headingId} className="text-heading text-default">
              {t("totp.heading")}
            </h1>
            <p className="text-body text-muted">{t("totp.subtitle")}</p>
          </>
        )}

        <p className="text-body text-muted">
          {recoveryMode ? t("totp.recovery_hint") : t("totp.code_hint")}
        </p>

        {serverErrorKey !== null && (
          <Alert variant="danger" description={t(serverErrorKey, { seconds: waitSeconds })} />
        )}
        {audienceError !== null && <AdminAudienceNotice serverTitle={audienceError} />}

        <FormField<TotpSchema, "code">
          name="code"
          label={recoveryMode ? t("totp.recovery_label") : t("totp.code_label")}
          required
          render={(field) => (
            <Input
              id={field.id}
              name={field.name}
              type="text"
              inputMode={recoveryMode ? "text" : "numeric"}
              pattern={recoveryMode ? undefined : "[0-9]*"}
              autoComplete="one-time-code"
              maxLength={recoveryMode ? 16 : 6}
              bidi="ltr"
              className={
                recoveryMode
                  ? "font-mono tabular-nums text-center tracking-[0.25em] uppercase"
                  : "font-mono tabular-nums text-center tracking-[0.5em]"
              }
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={(node) => {
                field.ref(node);
                codeRef.current = node;
              }}
              invalid={field.invalid}
              aria-describedby={field["aria-describedby"]}
              aria-invalid={field["aria-invalid"]}
              aria-required={field["aria-required"]}
            />
          )}
        />

        <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
          {t("totp.cta")}
        </Button>
        <div className="flex items-center justify-between gap-sm">
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            {t("totp.back")}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={toggleRecoveryMode}>
            {recoveryMode ? t("totp.use_authenticator") : t("totp.use_recovery")}
          </Button>
        </div>
      </Form>
    </FormProvider>
  );
}
